import { useMsal } from "@azure/msal-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  downloadStorageReport,
  listStorageReportJobs,
} from "../services/pdfService";
import { useI18n } from "../i18n";
import { isAdminAccount } from "../utils/accountRoles";
import ClientThumb from "../components/ClientThumb";
import "./StoragePage.css";

const DOWNLOADABLE_STATUSES = new Set(["completed", "completed_with_warnings"]);
const POLL_MS = 10000;

function formatLibraryDate(value) {
  if (!value) {
    return "-";
  }

  const asNumber = Number(value);
  const date = Number.isFinite(asNumber) ? new Date(asNumber) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("fr-FR");
}

function formatLibraryPeriod(job) {
  return `${formatLibraryDate(job?.startDate)} - ${formatLibraryDate(job?.endDate)}`;
}

function getDocumentLabel(job) {
  return String(job?.templateName || job?.title || "report").replace(/\.html$/i, "");
}

function getStorageStatusKey(job) {
  return String(job?.status || "").toLowerCase() === "completed_with_warnings"
    ? "storage_status_warning"
    : "storage_status_ok";
}

function getStorageStatusClassName(job) {
  return String(job?.status || "").toLowerCase() === "completed_with_warnings"
    ? "storage-status-pill storage-status-pill-warning"
    : "storage-status-pill storage-status-pill-success";
}

function openPdfBlob(blob) {
  const previewUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = previewUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => {
    window.URL.revokeObjectURL(previewUrl);
  }, 60_000);
}

const StoragePage = () => {
  const { instance, accounts } = useMsal();
  const { t } = useI18n();
  const account = accounts[0];
  const isAdmin = isAdminAccount(account);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [downloadingJobId, setDownloadingJobId] = useState(null);

  const getApiToken = useCallback(async () => {
    const accessToken = await instance.acquireTokenSilent({
      scopes: ["api://c247d402-5b9d-40f2-b7e6-b15a47c56f22/Api.access"],
      account,
    });
    return accessToken.accessToken;
  }, [account, instance]);

  const loadJobs = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);

      try {
        const token = await getApiToken();
        const data = await listStorageReportJobs({ token, limit: 500 });
        setJobs(Array.isArray(data.jobs) ? data.jobs : []);
      } catch (e) {
        setError(e.message || t("storage_load_error"));
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [getApiToken, t]
  );

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadJobs({ silent: true });
    }, POLL_MS);

    return () => window.clearInterval(interval);
  }, [loadJobs]);

  const downloadableJobs = useMemo(
    () => jobs.filter((job) => DOWNLOADABLE_STATUSES.has(String(job?.status || "").toLowerCase())),
    [jobs]
  );

  const documents = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return downloadableJobs
      .filter((job) => clientFilter === "all" || job.clientName === clientFilter)
      .filter((job) => {
        if (!normalizedSearch) {
          return true;
        }

        const haystack = [
          getDocumentLabel(job),
          job?.clientName,
          job?.ownerEmail,
          formatLibraryPeriod(job),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(normalizedSearch);
      })
      .sort((left, right) => {
        const leftTimestamp = new Date(left.completedAt || left.createdAt || 0).getTime();
        const rightTimestamp = new Date(right.completedAt || right.createdAt || 0).getTime();
        return rightTimestamp - leftTimestamp;
      });
  }, [clientFilter, downloadableJobs, search]);

  const clientOptions = useMemo(
    () =>
      Array.from(new Set(downloadableJobs.map((job) => job.clientName).filter(Boolean))).sort(
        (left, right) => String(left).localeCompare(String(right), "fr")
      ),
    [downloadableJobs]
  );

  const handleViewPdf = useCallback(
    async (job) => {
      if (!job?.jobId || downloadingJobId) {
        return;
      }

      setDownloadingJobId(job.jobId);
      setError(null);

      try {
        const token = await getApiToken();
        const response = await downloadStorageReport(job.jobId, { token });
        openPdfBlob(response.blob);
      } catch (e) {
        setError(e.message || t("storage_open_error"));
      } finally {
        setDownloadingJobId(null);
      }
    },
    [downloadingJobId, getApiToken, t]
  );

  return (
    <div className="storage-page">
      <div className="storage-header">
        <div className="storage-header-left">
          <div className="storage-count">
            <strong>{documents.length}</strong>
            <span>{t("storage_documents_label")}</span>
          </div>
          <p className="storage-description">
            {isAdmin ? t("storage_admin_description") : t("storage_user_description")}
          </p>
        </div>
        <button className="button-generate" onClick={() => loadJobs()} disabled={isLoading}>
          {isLoading ? (
            <span className="btn-loading-content">
              <span className="button-spinner" aria-hidden="true" />
              {t("loading")}
            </span>
          ) : (
            t("refresh")
          )}
        </button>
      </div>

      {error && <p className="storage-error">{error}</p>}

      <div className={`storage-filters ${isAdmin ? "" : "storage-filters-single"}`.trim()}>
        <input
          className="storage-search"
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={isAdmin ? t("storage_search_admin") : t("storage_search_user")}
        />
        {isAdmin && (
          <select value={clientFilter} onChange={(event) => setClientFilter(event.target.value)}>
            <option value="all">{t("all_clients")}</option>
            {clientOptions.map((clientName) => (
              <option key={clientName} value={clientName}>
                {clientName}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="storage-table-wrap">
        <table className="storage-table">
          <thead>
            <tr>
              <th>{t("client")}</th>
              <th>Template</th>
              <th>{t("period")}</th>
              <th>{t("storage_available_date")}</th>
              <th>{t("status")}</th>
              {isAdmin && <th>{t("user")}</th>}
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="storage-loading-cell">
                  <span className="btn-loading-content">
                    <span className="list-spinner" />
                    {t("loading")}
                  </span>
                </td>
              </tr>
            )}
            {!isLoading && documents.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="storage-empty-cell">
                  {t("storage_empty_title")}
                </td>
              </tr>
            )}
            {!isLoading &&
              documents.map((job) => {
                const isDownloading = downloadingJobId === job.jobId;

                return (
                  <tr key={job.jobId}>
                    <td className="storage-cell-client">
                      <div className="storage-client-cell">
                        <ClientThumb clientName={job.clientName} getApiToken={getApiToken} size={18} />
                        {job.clientName || "-"}
                      </div>
                    </td>
                    <td className="storage-cell-template">{getDocumentLabel(job)}</td>
                    <td className="storage-cell-period">{formatLibraryPeriod(job)}</td>
                    <td>{formatLibraryDate(job.completedAt || job.createdAt)}</td>
                    <td>
                      <span className={getStorageStatusClassName(job)}>
                        {t(getStorageStatusKey(job))}
                      </span>
                    </td>
                    {isAdmin && <td className="storage-cell-user">{job.ownerEmail || "-"}</td>}
                    <td>
                      <button
                        className="storage-view-button"
                        onClick={() => handleViewPdf(job)}
                        disabled={Boolean(downloadingJobId)}
                      >
                        {isDownloading ? t("open") : t("view")}
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StoragePage;
